import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Textarea,
  Text,
  Box,
  VStack,
  HStack,
  Checkbox,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner,
} from "@chakra-ui/react";
import {
  saveUserTemplate,
  updateUserTemplate,
  saveUserDefaultTemplate,
  loadSavedUserTemplates,
  shareTemplateToShared,
  loadSharedTemplates,
  importSharedTemplate,
} from "../utils/formProcessingUtils";

/**
 * Shared Process Form Prompt Editor modal component.
 * Used by PatientPage, EditableDocumentPage, and SchedulesTable.
 *
 * Props:
 *   isOpen - whether the modal is visible
 *   onClose - called when the modal is closed
 *   onRun({ fullPrompt, editablePrompt, templateInfo, useStoredFullText, forceProcessForms }) - called when the user clicks Run
 *   patientType - "NP", "NS", "FFP", or "FFS"
 *   initialData - output of prepareFormProcessingData(): { templateInfo, editablePrompt, useStoredFullText, forceProcessForms, savedTemplates, templateTypeName }
 *   toast - Chakra UI toast function
 */
const ProcessFormPromptEditor = ({
  isOpen,
  onClose,
  onRun,
  onDocAsTemplate,
  patientType,
  initialData,
  toast,
}) => {
  const [editablePrompt, setEditablePrompt] = useState("");
  const [templateInfo, setTemplateInfo] = useState("");
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [originalTemplate, setOriginalTemplate] = useState("");
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [defaultTemplate, setDefaultTemplate] = useState("");
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(-1);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [useStoredFullText, setUseStoredFullText] = useState(false);
  const [forceProcessForms, setForceProcessForms] = useState(false);

  // Shared templates state
  const [sharedTemplates, setSharedTemplates] = useState([]);
  const [showShared, setShowShared] = useState(false);
  const [isLoadingShared, setIsLoadingShared] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Log z-index info when modal opens for debugging
  useEffect(() => {
    if (isOpen) {
      console.log("🔷 ProcessFormPromptEditor: modal opened");
      setTimeout(() => {
        const overlay = document.querySelector(".chakra-modal__overlay");
        const contentContainer = document.querySelector(
          ".chakra-modal__content-container",
        );
        const content = document.querySelector("[role='dialog']");
        console.log("🔷 ProcessFormPromptEditor DOM check:", {
          overlay: overlay
            ? { zIndex: window.getComputedStyle(overlay).zIndex, el: overlay }
            : "not found",
          contentContainer: contentContainer
            ? {
                zIndex: window.getComputedStyle(contentContainer).zIndex,
                el: contentContainer,
              }
            : "not found",
          content: content
            ? { zIndex: window.getComputedStyle(content).zIndex }
            : "not found",
        });
      }, 100);
    }
  }, [isOpen]);

  // Initialize state from initialData when modal opens
  useEffect(() => {
    if (isOpen && initialData) {
      setEditablePrompt(initialData.editablePrompt || "");
      setTemplateInfo(initialData.templateInfo || "");
      setOriginalPrompt(initialData.editablePrompt || "");
      setOriginalTemplate(initialData.templateInfo || "");
      setDefaultPrompt(initialData.editablePrompt || "");
      setDefaultTemplate(initialData.templateInfo || "");
      setSavedTemplates(initialData.savedTemplates || []);
      setSelectedTemplateIndex(-1);
      setUseStoredFullText(initialData.useStoredFullText || false);
      setForceProcessForms(initialData.forceProcessForms || false);
      setShowSaveDialog(false);
    }
  }, [isOpen, initialData]);

  const hasChanges = () => {
    return (
      editablePrompt !== originalPrompt || templateInfo !== originalTemplate
    );
  };

  const handleClose = () => {
    setSelectedTemplateIndex(-1);
    onClose();
  };

  const handleRunClick = () => {
    if (!editablePrompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a prompt",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (hasChanges()) {
      setShowSaveDialog(true);
      return;
    }

    proceedWithRun();
  };

  const proceedWithRun = () => {
    const fullPrompt = `${editablePrompt}\n\n${templateInfo}`;
    setShowSaveDialog(false);
    handleClose();
    onRun({
      fullPrompt,
      editablePrompt,
      templateInfo,
      useStoredFullText,
      forceProcessForms,
    });
  };

  const handleSaveAsNewAndRun = async () => {
    try {
      await saveUserTemplate(patientType, templateInfo, editablePrompt);
      toast({
        title: "Template Saved",
        description: "Your template and prompt have been saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      const saved = await loadSavedUserTemplates(patientType);
      setSavedTemplates(saved);
      setOriginalTemplate(templateInfo);
      setOriginalPrompt(editablePrompt);

      setShowSaveDialog(false);
      proceedWithRun();
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: "Save Error",
        description: `Failed to save template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUpdateCurrentAndRun = async () => {
    try {
      if (selectedTemplateIndex < 0) {
        throw new Error("No template selected to update");
      }

      await updateUserTemplate(
        patientType,
        templateInfo,
        editablePrompt,
        selectedTemplateIndex,
      );
      toast({
        title: "Template Updated",
        description: "Your template and prompt have been updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      const saved = await loadSavedUserTemplates(patientType);
      setSavedTemplates(saved);
      setOriginalTemplate(templateInfo);
      setOriginalPrompt(editablePrompt);

      setShowSaveDialog(false);
      proceedWithRun();
    } catch (error) {
      console.error("Error updating template:", error);
      toast({
        title: "Update Error",
        description: `Failed to update template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSaveAsDefaultAndRun = async () => {
    try {
      await saveUserDefaultTemplate(patientType, templateInfo, editablePrompt);
      toast({
        title: "Default Saved",
        description:
          "Your personal default template and prompt have been saved",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setDefaultTemplate(templateInfo);
      setDefaultPrompt(editablePrompt);
      setOriginalTemplate(templateInfo);
      setOriginalPrompt(editablePrompt);

      setShowSaveDialog(false);
      proceedWithRun();
    } catch (error) {
      console.error("Error saving default template:", error);
      toast({
        title: "Save Error",
        description: `Failed to save default template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle sharing a template to shared templates
  const handleShareToShared = async (templateName) => {
    if (!templateName || !templateName.trim()) {
      toast({
        title: "Template Name Required",
        description: "Please enter a name for this template to share",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!editablePrompt.trim() || !templateInfo.trim()) {
      toast({
        title: "Template Content Required",
        description:
          "Please create a template with prompt and content before sharing",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSharing(true);
    try {
      await shareTemplateToShared(
        patientType,
        templateInfo,
        editablePrompt,
        templateName,
      );

      toast({
        title: "Template Shared!",
        description: `"${templateName}" is now available in the Shared Templates for all users`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      // Refresh shared templates
      loadSharedTemplatesFromShared();
    } catch (error) {
      toast({
        title: "Share Error",
        description: error.message || "Failed to share template",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSharing(false);
    }
  };

  // Load shared templates
  const loadSharedTemplatesFromShared = async () => {
    setIsLoadingShared(true);
    try {
      const templates = await loadSharedTemplates(patientType);
      setSharedTemplates(templates || []);
    } catch (error) {
      console.error("Error loading shared templates:", error);
      setSharedTemplates([]);
    } finally {
      setIsLoadingShared(false);
    }
  };

  // Handle importing a shared template
  const handleImportTemplate = async (sharedTemplate) => {
    try {
      const result = await importSharedTemplate(
        patientType,
        sharedTemplate.fileName,
      );

      toast({
        title: "Template Imported!",
        description: `"${sharedTemplate.name}" by ${sharedTemplate.author} has been added to your templates`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      // Reload user's templates
      const saved = await loadSavedUserTemplates(patientType);
      setSavedTemplates(saved);
    } catch (error) {
      toast({
        title: "Import Error",
        description: error.message || "Failed to import template",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleTabChange = (index) => {
    if (index === 0) {
      const promptToUse = defaultPrompt || originalPrompt;
      const templateToUse = defaultTemplate || originalTemplate;
      setEditablePrompt(promptToUse);
      setTemplateInfo(templateToUse);
      setSelectedTemplateIndex(-1);
      setOriginalPrompt(promptToUse);
      setOriginalTemplate(templateToUse);
    } else {
      const templateIndex = index - 1;
      const template = savedTemplates[templateIndex];
      if (template) {
        setEditablePrompt(template.prompt);
        setTemplateInfo(template.template);
        setSelectedTemplateIndex(templateIndex);
        setOriginalPrompt(template.prompt);
        setOriginalTemplate(template.template);
      }
    }
  };

  const renderTabContent = () => (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Text fontWeight="semibold" mb={2} fontSize="sm" color="gray.700">
          Editable Prompt (You can modify this):
        </Text>
        <Textarea
          value={editablePrompt}
          onChange={(e) => setEditablePrompt(e.target.value)}
          placeholder="Enter your prompt here..."
          rows={8}
          resize="vertical"
          fontFamily="mono"
          fontSize="sm"
        />
      </Box>

      <Box>
        <VStack align="stretch" spacing={3}>
          <Checkbox
            isChecked={useStoredFullText}
            onChange={(e) => {
              const checked = e.target.checked;
              setUseStoredFullText(checked);
              setForceProcessForms(!checked);
            }}
            colorScheme="blue"
          >
            <Text fontSize="sm">Use stored form data from database</Text>
          </Checkbox>
          <Text fontSize="xs" color="gray.500" mt={-2} ml={6}>
            When checked, uses the stored full_text from the database
          </Text>

          <Checkbox
            isChecked={forceProcessForms}
            onChange={(e) => {
              const checked = e.target.checked;
              setForceProcessForms(checked);
              setUseStoredFullText(!checked);
            }}
            colorScheme="blue"
          >
            <Text fontSize="sm">Re-extract form text and update database</Text>
          </Checkbox>
          <Text fontSize="xs" color="gray.500" mt={-2} ml={6}>
            When checked, re-extracts form text and updates full_text and
            summary fields in the database
          </Text>
        </VStack>
      </Box>

      <Box>
        <HStack justify="space-between" mb={2}>
          <Text fontWeight="semibold" fontSize="sm" color="gray.700">
            Template Information (Editable):
          </Text>
          {onDocAsTemplate && (
            <Button
              size="xs"
              colorScheme="purple"
              variant="outline"
              title="Instead of using this template, the AI will edit the existing document directly using the form data."
              onClick={() => {
                handleClose();
                onDocAsTemplate({
                  editablePrompt,
                  useStoredFullText,
                  forceProcessForms,
                });
              }}
              isDisabled={!editablePrompt.trim()}
            >
              Use Doc as Template
            </Button>
          )}
        </HStack>
        <Textarea
          value={templateInfo}
          onChange={(e) => setTemplateInfo(e.target.value)}
          placeholder="Enter template here..."
          rows={15}
          resize="vertical"
          fontFamily="mono"
          fontSize="sm"
        />
      </Box>
    </VStack>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="xl"
        blockScrollOnMount={false}
      >
        <ModalOverlay style={{ zIndex: 10001 }} />
        <ModalContent
          maxH="90vh"
          overflowY="auto"
          containerProps={{ zIndex: 10002 }}
        >
          <ModalHeader>Edit Process form Prompt</ModalHeader>
          <ModalCloseButton />
          <Box
            px={6}
            pt={2}
            pb={2}
            borderBottomWidth="1px"
            display="flex"
            alignItems="center"
            gap={3}
          >
            <Button
              colorScheme="blue"
              onClick={handleRunClick}
              isDisabled={!editablePrompt.trim()}
            >
              Run Process form
            </Button>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="outline"
              colorScheme="teal"
              onClick={() => {
                setShowShared(true);
                loadSharedTemplatesFromShared();
              }}
              ml="auto"
              size="sm"
            >
              Shared{" "}
              {sharedTemplates.length > 0 && `(${sharedTemplates.length})`}
            </Button>
          </Box>
          <ModalBody>
            <Tabs
              index={
                selectedTemplateIndex === -1 ? 0 : selectedTemplateIndex + 1
              }
              onChange={handleTabChange}
            >
              <TabList>
                <Tab>Default</Tab>
                {savedTemplates.map((template, index) => (
                  <Tab key={index}>{template.name}</Tab>
                ))}
              </TabList>
              <TabPanels>
                <TabPanel>{renderTabContent()}</TabPanel>
                {savedTemplates.map((template, index) => (
                  <TabPanel key={index}>{renderTabContent()}</TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={showSaveDialog}
        leastDestructiveRef={undefined}
        onClose={() => setShowSaveDialog(false)}
        isCentered
      >
        <AlertDialogOverlay style={{ zIndex: 10003 }}>
          <AlertDialogContent maxW="550px" containerProps={{ zIndex: 10004 }}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Save Template Changes?
            </AlertDialogHeader>
            <AlertDialogBody>
              You've made changes to the template or prompt. How would you like
              to save these changes?
            </AlertDialogBody>
            <AlertDialogFooter flexWrap="wrap" gap={2}>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowSaveDialog(false);
                  proceedWithRun();
                }}
              >
                Don't Save
              </Button>
              {selectedTemplateIndex >= 0 && (
                <Button
                  colorScheme="green"
                  onClick={handleUpdateCurrentAndRun}
                  fontSize="sm"
                >
                  Update Current & Run
                </Button>
              )}
              <Button
                colorScheme="blue"
                onClick={handleSaveAsNewAndRun}
                fontSize="sm"
              >
                Save as New & Run
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleSaveAsDefaultAndRun}
                fontSize="sm"
              >
                Save as new Default
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Template Shared Modal */}
      <Modal
        isOpen={showShared}
        onClose={() => setShowShared(false)}
        size="xl"
        isCentered
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent maxH="80vh" overflowY="auto">
          <ModalHeader>
            <VStack align="start" spacing={1}>
              <Text>Template Shared</Text>
              <Text fontSize="xs" color="gray.500" fontWeight="normal">
                Browse and import templates shared by other users
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            {isLoadingShared ? (
              <Box py={8} textAlign="center">
                <Spinner size="lg" color="teal.500" />
                <Text mt={4} color="gray.600">
                  Loading shared templates...
                </Text>
              </Box>
            ) : sharedTemplates.length === 0 ? (
              <Box py={8} textAlign="center">
                <Text color="gray.500" mb={2}>
                  No shared templates yet.
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Be the first to share your template to the Shared Templates!
                </Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={3}>
                {sharedTemplates.map((template) => (
                  <Box
                    key={template.fileName}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor="gray.200"
                    _hover={{ borderColor: "teal.300", bg: "gray.50" }}
                  >
                    <HStack justify="space-between" mb={1}>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="semibold" fontSize="sm">
                          {template.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          by {template.author}
                        </Text>
                      </VStack>
                      <Button
                        colorScheme="teal"
                        size="sm"
                        onClick={() => handleImportTemplate(template)}
                      >
                        Import
                      </Button>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="outline" onClick={() => setShowShared(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ProcessFormPromptEditor;
